import { assign, createMachine } from 'xstate';

export interface MentorCheckoutInfo {
  first_name: string;
  last_name: string;
  timezone: string;
  cost: number;
  original_hourly_rate: number;
  discount_code?: string;
  rating: number;
  no_sessions: number;
  mentor_id: string;
  slug: string;
  profile_image_url: string;
  availabilityDetails: {
    hour: boolean;
    halfHour: boolean;
  };
}

export const DATES_PER_PAGE = 3;

const totalPages = (pages: number): number =>
  Math.ceil(pages / DATES_PER_PAGE);

type SELECT_EVENT = { type: 'SELECT'; data: { selected: string } };
type TOGGLE_EXPANDED_EVENT = {
  type: 'TOGGLE_EXPANDED';
  data: { index: number };
};

type INCREMENT_EVENT = { type: 'NEXT' };
type DECREMENT_EVENT = { type: 'PREVIOUS' };

export type AvailabilitySelectionEvents =
  | INCREMENT_EVENT
  | DECREMENT_EVENT
  | TOGGLE_EXPANDED_EVENT
  | SELECT_EVENT
  | { type: 'TOGGLE_DURATION' };

const incrementStartIndex = (
  duration:
    | 'thirty_minute_page_start_index'
    | 'sixty_minute_page_start_index'
) =>
  assign(
    (
      context: AvailabilitySelectionContext,
      event: INCREMENT_EVENT
    ) => ({
      [duration]: context[duration] + DATES_PER_PAGE,
    })
  );

const decrementStartIndex = (
  duration:
    | 'thirty_minute_page_start_index'
    | 'sixty_minute_page_start_index'
) =>
  assign(
    (
      context: AvailabilitySelectionContext,
      event: DECREMENT_EVENT
    ) => ({
      [duration]: context[duration] - DATES_PER_PAGE,
    })
  );

export interface AvailabilitySelectionContext {
  thirty_minute_slots: string[][];
  sixty_minute_slots: string[][];
  thirty_minute_page_start_index: number;
  sixty_minute_page_start_index: number;
  thirty_minute_expanded_index: number;
  sixty_minute_expanded_index: number;
  selected: string;
  mentor_info: MentorCheckoutInfo;
  duration: 30 | 60;
}

const createAvailabilitySelectionMachine = ({
  thirty_minute_slots,
  sixty_minute_slots,
  mentor_info,
}: {
  thirty_minute_slots: string[][];
  sixty_minute_slots: string[][];
  mentor_info: MentorCheckoutInfo;
}) =>
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QCED2qDWBLAdlAggG4CGWANsQEblYAuAngHS0AWWATgwPoC2uArrTBdiAB1GpctHmBy1YAYgAqAeQDiagDIBRLgBEAqgCV8SgJIqAcgG0ADAF1EoCbDpZUOJyAAeiAIwAbH4A7IzBAMzhAKzBUQAcgQBMfnHBADQg9IjBfomMicEAnJG2AeGFtrZRACwAvrUZaJi4BCTkVDQMzGyc9LwCQiLiknIycooAyto6AMJKdo5IIC5uHl6+CH7RcYx+uVFRAdVRiQk1GVmb1SGMQbbBQdflAXH1jejYeESkFNRkdExWBxuHwcIJhGIJFIxvJGKJiFAWsp1FpdNoABoABXwlj02j0Cy8K1o7k8Sw2eyi4UYB2qFQCNSpx2qF38pVCtjiUUKZUKiTpISibxATU+rR+HX+XSBvX6YMGkJG0lksPhiLwjFgtGInAUlgx8wcRNQrhJa3J-jiO1s4X2rM2IWqjASiSiWz81VsfkKgWFopa33afwB3WBfVB4KGUNGKtgcIRLXj6uIJLweoNhKWxNJ60QB0KuyqZWqDziPNi9p5tkY1WqiQCFXC-NrhVeDRFHwDbV+nUBPRBAwhw2hsaTibVuBTSP16PmfkWzhNqzJoA2cUSeQiteCNpdtmqcXtfn3ARptnrAUqlXCxyF7f9X27kpDMoH8qH0eV4zHGonOCnaaYkY2gAGoWAYEyZoupo5haCAnFEtxNq2-LBIk9znJkiBxGUjCtoUbpupy54BIkfqdo+ErBtK-bhoOUZKjCcZ-lAP4AVAChAaB4GQfOxoweaq6IAUoR8t60QxHExw5JW5Q1gEpGlMyRwpORzSUUGvahrKEYKsOMbfixjAUFqnHAWBKgQVByxLmaK4+IgFR+LsRRSeJ1QBA86RYQgBHVvuPLhHEEmefyalioGPZSn2YZypGiojt+YDePCOAQJAyIaDoXAYtiuL4tZ2aCQ5vneZcnlOhEhTHCEhTBCWkThV2VFaa43hvvF+lfvImWovoximBYNhGlmtmwUJmwxIhZY5HEVQxK6fhHiEzlNocyHJLWClNRpUUhm1HV6Z+TEKFMsyGguNkCfZGwbqcjDhKUNpFOewWJEeQU7HWJZoY9l58jt4qadFmpYO1dHvgxiWqgmaaqFlaJYjieIEiN0HLrmCCJDyzmlue6F1lahxHuyjCcvE4TBFysSNfeFFA3tXQHRDnXHaORlajqtDprOhVjcVly5FUzq5EFO5oXNEQZBSVpk7arqA5Fz5M2Dh0fox7Ow6xf7sTzF38RjcGJOEnkPSk647icQXhJWl41nWKn7tjnrBIrT7UUwzNxUdGuGVrbGphxM5zpdRU3cJZTVtVwRVccAXEz5x4eWe-INtVDVkXT6kM8rnuqyzPvQ8x-s64HZncZZkFo1dhsTcbkTOh5jqxDUFRldhuH4TU9ai3ydRZxF7utfn3vq0XP7a7DutcRZVl8aN12Y+hOG7NEBNHDHP2yQWHmXjal5bOumfvNnSse6D4Oj1DBkw+qrEmdzM88Xzi9GzuBblI9N7JHNfJRJWUR-J0kpn4N01xgp3hPoPFqIMva6THjfOMyVUrpQgL1bKuVkYFWrmHTG3phbJBKPVUiG4cL2gqmEco3JaxzQZGFYUOBUDpXgEsB8OcPYGzspjAAtB6FkPk6QFljlTK0XIXiHDdjAl8tEr4JUQZw8aJVuE8ntPEUIxtqpbFKEFBk-coHNWBtI2K8Dr7dWLnfBRxUNjcIPPaGOp4DzRzKPVcBkCOynyHiDV8BcEFmInpqbUnBLHhwQLYI86Fpr7HdJ6b0voB4GMZjFHS9E5F+KMqXFowTMaXh2NycoMcY5UnXPwwWpRnJlkkqkVuKFJGGJosYlJXUmL+IflkuCSd-I1GCk9cmQUSZHAegRMsewTaBDdLUxJ2k1amOaUZNgWpUDsEuOjLh7TKbLVsAWF0bpbQxJ9AECZucpk+JmaOZBxA0qQDaRNLYDJbibO5FyHC540LkOCg9Uh3ocgvOOIc8+sAyCoFoFwWAYAyBgAAMZCAgNckqKQti7AUqkCIpw+QmyPHWRC+5Ah92uCkPR7joF1LzpfExqSmKwusSonyaj8hUIOPVHctY3FsLPsPUljS2Z+wsQvWuSjbE+XsTWCpu4qZUw8n89l0zyWazvgErmlLEBhMThEkWxtUj3FOPccIkrYEjzJU02V44p6B0VVja4p5JabKbGhPk-J+nlIIsIm8wzdX7X1Zy32t9EytN5asuuUkdj1jmqQqS9ZCj9KdFQ51ji-BupVhyyGMruWJnmbQRZyya7+rhdVRChDHrELumQxOx4tmiw1RLbV8aSXSsNUlFKFzUFmttBEZ0G4cieggS8N51ImyoqklUDcdZ6j1CAA */
      context: {
        thirty_minute_slots: thirty_minute_slots,
        sixty_minute_slots: sixty_minute_slots,
        thirty_minute_page_start_index: 0,
        sixty_minute_page_start_index: 0,
        thirty_minute_expanded_index: 0,
        sixty_minute_expanded_index: 0,
        selected: '',
        mentor_info,
        duration: mentor_info.availabilityDetails.halfHour ? 30 : 60,
      },
      id: 'BookingAvailability',
      initial: mentor_info.availabilityDetails.halfHour
        ? 'thirty_minute_appointments'
        : 'sixty_minute_appointments',
      states: {
        thirty_minute_appointments: {
          initial: 'paging',
          states: {
            paging: {
              initial: 'start',
              states: {
                start: {
                  on: {
                    NEXT: {
                      target: 'paginating',
                      actions: incrementStartIndex(
                        'thirty_minute_page_start_index'
                      ),
                    },
                  },
                },
                paginating: {
                  on: {
                    NEXT: [
                      {
                        cond: 'hasNextPage',
                        internal: true,
                        actions: incrementStartIndex(
                          'thirty_minute_page_start_index'
                        ),
                      },
                      {
                        target: 'last',
                        actions: incrementStartIndex(
                          'thirty_minute_page_start_index'
                        ),
                      },
                    ],
                    PREVIOUS: [
                      {
                        cond: 'hasPrecedingPage',
                        internal: true,
                        actions: decrementStartIndex(
                          'thirty_minute_page_start_index'
                        ),
                      },
                      {
                        target: 'start',
                        actions: decrementStartIndex(
                          'thirty_minute_page_start_index'
                        ),
                      },
                    ],
                  },
                },
                last: {
                  on: {
                    PREVIOUS: {
                      target: 'paginating',
                      actions: decrementStartIndex(
                        'thirty_minute_page_start_index'
                      ),
                    },
                  },
                },
                history: {
                  history: 'shallow',
                  type: 'history',
                },
              },
              on: {
                TOGGLE_EXPANDED: {
                  target: 'expanded',
                  actions: assign(
                    (_, event: TOGGLE_EXPANDED_EVENT) => ({
                      thirty_minute_expanded_index: event.data.index,
                    })
                  ),
                },
              },
            },
            expanded: {
              on: {
                TOGGLE_EXPANDED: {
                  target:
                    '#BookingAvailability.thirty_minute_appointments.paging.history',
                },
              },
            },
          },
          on: {
            TOGGLE_DURATION: {
              target:
                '#BookingAvailability.sixty_minute_appointments.paging.history',
            },
            SELECT: {
              target: 'slot_selected',
              actions: ['setSelected', assign({ duration: 30 })],
            },
          },
        },

        slot_selected: {
          type: 'final',
          data: (context, _) => ({
            selected: context.selected,
            duration: context.duration,
          }),
        },

        sixty_minute_appointments: {
          initial: 'paging',
          states: {
            paging: {
              initial: 'start',
              states: {
                start: {
                  on: {
                    NEXT: {
                      target: 'paginating',
                      actions: incrementStartIndex(
                        'sixty_minute_page_start_index'
                      ),
                    },
                  },
                },
                paginating: {
                  on: {
                    NEXT: [
                      {
                        cond: 'hasNextPage',
                        internal: true,
                        actions: incrementStartIndex(
                          'sixty_minute_page_start_index'
                        ),
                      },
                      {
                        target: 'last',
                        actions: incrementStartIndex(
                          'sixty_minute_page_start_index'
                        ),
                      },
                    ],
                    PREVIOUS: [
                      {
                        cond: 'hasPrecedingPage',
                        internal: true,
                        actions: decrementStartIndex(
                          'sixty_minute_page_start_index'
                        ),
                      },
                      {
                        target: 'start',
                        actions: decrementStartIndex(
                          'sixty_minute_page_start_index'
                        ),
                      },
                    ],
                  },
                },
                last: {
                  on: {
                    PREVIOUS: {
                      target: 'paginating',
                      actions: decrementStartIndex(
                        'sixty_minute_page_start_index'
                      ),
                    },
                  },
                },
                history: {
                  history: 'shallow',
                  type: 'history',
                },
              },
              on: {
                TOGGLE_EXPANDED: {
                  target: 'expanded',
                  actions: assign(
                    (_, event: TOGGLE_EXPANDED_EVENT) => ({
                      sixty_minute_expanded_index: event.data.index,
                    })
                  ),
                },
              },
            },
            expanded: {
              on: {
                TOGGLE_EXPANDED: {
                  target:
                    '#BookingAvailability.sixty_minute_appointments.paging.history',
                },
              },
            },
          },
          on: {
            TOGGLE_DURATION: {
              target:
                '#BookingAvailability.thirty_minute_appointments.paging.history',
            },
            SELECT: {
              target: 'slot_selected',
              actions: ['setSelected', assign({ duration: 60 })],
            },
          },
        },
      },
      schema: {
        events: {} as AvailabilitySelectionEvents,
        context: {} as AvailabilitySelectionContext,
      },
      predictableActionArguments: true,
      preserveActionOrder: true,
    },
    {
      actions: {
        setSelected: assign((_, event: SELECT_EVENT) => ({
          selected: event.data.selected,
        })),
      },
      guards: {
        hasNextPage: (context, _, { state }) => {
          const is_thirty_minutes = state.matches(
            'thirty_minute_appointments'
          );
          const dates = is_thirty_minutes
            ? context.thirty_minute_slots
            : context.sixty_minute_slots;
          const current_page = is_thirty_minutes
            ? context['thirty_minute_page_start_index']
            : context['sixty_minute_page_start_index'];
          const total_pages = totalPages(dates.length);
          // are you before the 2nd last page.
          return current_page + DATES_PER_PAGE < total_pages;
        },
        hasPrecedingPage: (context, _, { state }) => {
          const is_thirty_minutes = state.matches(
            'thirty_minute_appointments'
          );
          const current_page = is_thirty_minutes
            ? context['thirty_minute_page_start_index']
            : context['sixty_minute_page_start_index'];
          return current_page > DATES_PER_PAGE;
        },
      },
      delays: {},
    }
  );

export default createAvailabilitySelectionMachine;
